import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Grid, Stack, Paper, RadioGroup, FormControlLabel, Radio, Select, MenuItem, FormControl, InputLabel, IconButton, Button, Modal } from '@mui/material';
import Edit from '@mui/icons-material/Edit';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import SaveButton from '../Components/SaveButton';
import moment from 'moment';
import axios from 'axios';
import { APIURL, CERBOSURL } from '../../../config';
import entities from '../../../resources.json';
import LAYOUT_CONFIGURATIONS from '../../GridLayoutComponent/layoutConfigurations';

// One entry per widget in GridLayoutComponent's componentMap.
//   label      — must match the componentMap key exactly; this is the string
//                persisted in gridLayout.layoutContent and looked up at render.
//   permission — resources.json id prefix for the matching Home rule in
//                authPolicy/policies/page_policies/Home.yaml.
// Home.yaml is the single source of truth for which module subscriptions and
// lines of business a widget needs, so this list carries no business-type
// filtering of its own — everything here is offered if Cerbos allows it.
const HOME_WIDGETS = [
    { label: 'Upcoming Appointments', permission: 'UpcomingAppointmentsHome' },
    { label: 'Upcoming Tasks', permission: 'UpcomingTasksHome' },
    { label: 'Recent Notes', permission: 'RecentNotesHome' },
    { label: 'Recent Calls', permission: 'RecentCallsHome' },
    { label: 'SC Customers', permission: 'SCCustomersHome' },
    { label: 'SC Management', permission: 'SCManagementHome' },
    { label: 'Scheduler', permission: 'SchedulerHome' },
    { label: 'Timesheets', permission: 'TimesheetsHome' },
    { label: 'Time Management Summary', permission: 'TimeManagementSummaryHome' },
    { label: 'EVV Submission Review', permission: 'EVVSubmissionReviewHome' },
    { label: 'Locate', permission: 'LocateHome' },
    { label: 'Document Center', permission: 'DocumentCenterHome' },
    { label: 'BI & Reporting', permission: 'BIAndReportingHome' },
    { label: 'Billing', permission: 'BillingHome' },
    { label: 'Claims', permission: 'ClaimsHome' },
    { label: 'Remittance', permission: 'RemittanceHome' },
    { label: 'Financial Summary', permission: 'FinancialSummaryHome' },
    { label: 'Home Dashboard KPIs', permission: 'HomeDashboardKPIsHome' },
    { label: 'Shift Map', permission: 'ShiftMapHome' },
    { label: 'Alerts', permission: 'AlertsHome' },
    { label: 'Billing Summary Chart', permission: 'BillingSummaryChartHome' },
    { label: 'Caregiver Turnover Chart', permission: 'CaregiverTurnoverChartHome' },
    { label: 'Net Promoter Score', permission: 'NetPromoterScoreHome' },
    { label: 'Monitor Delays', permission: 'MonitorDelaysHome' },
    { label: 'Bottleneck Tasks', permission: 'BottleneckTasksHome' },
    { label: 'Employee Load', permission: 'EmployeeLoadHome' },
    { label: 'Workflow Instances', permission: 'WorkflowInstancesHome' },
    { label: 'AI Companion', permission: 'AICompanionHome' },
];

// Layout Preview Component
//
// Geometry mirrors the real layouts: 12 columns, and row weights taken from the
// card heights GridLayoutComponent actually renders. Each card is
// [columnStart, columnSpan, rowStart, rowSpan], 1-indexed as CSS grid lines.
//
// Drawn with CSS grid rather than percentage-positioned boxes so the spacing comes from `gap`.
//
// One entry per key in LAYOUT_CONFIGURATIONS — see the checklist there for how to add a layout type.
const LAYOUT_PREVIEWS = {
    '1 X 1': { rows: [1], cards: [[1, 12, 1, 1]] },
    '1 X 2': { rows: [1], cards: [[1, 6, 1, 1], [7, 6, 1, 1]] },
    '2 X 1': { rows: [1, 1], cards: [[1, 12, 1, 1], [1, 12, 2, 1]] },
    '2 X 2': { rows: [1, 1], cards: [[1, 6, 1, 1], [7, 6, 1, 1], [1, 6, 2, 1], [7, 6, 2, 1]] },
    '2 X (1, 2)': { rows: [1, 1], cards: [[1, 12, 1, 1], [1, 6, 2, 1], [7, 6, 2, 1]] },
    '(1, 2) X 2': { rows: [1, 1], cards: [[1, 6, 1, 2], [7, 6, 1, 1], [7, 6, 2, 1]] },
    '3 X (1, 2, 3)': {
        rows: [1, 4, 3],
        cards: [[1, 12, 1, 1], [1, 6, 2, 1], [7, 6, 2, 1], [1, 4, 3, 1], [5, 4, 3, 1], [9, 4, 3, 1]],
    },
};

const LayoutPreview = ({ layoutType }) => {
    const preview = LAYOUT_PREVIEWS[layoutType];

    if (!preview) {
        return null;
    }

    return (
        <Paper
            variant="outlined"
            sx={{
                width: '100%',
                height: 100,
                display: 'grid',
                gridTemplateColumns: 'repeat(12, 1fr)',
                gridTemplateRows: preview.rows.map((weight) => `${weight}fr`).join(' '),
                gap: '2px',
                padding: '2px',
                boxSizing: 'border-box',
                border: '1px solid rgba(0,0,0,0.2)',
                backgroundColor: 'rgba(0,0,0,0.05)'
            }}
        >
            {preview.cards.map(([colStart, colSpan, rowStart, rowSpan], index) => (
                <Box
                    key={index}
                    sx={{
                        gridColumn: `${colStart} / span ${colSpan}`,
                        gridRow: `${rowStart} / span ${rowSpan}`,
                        minWidth: 0,
                        minHeight: 0,
                        overflow: 'hidden',
                        backgroundColor: 'rgba(25, 118, 210, 0.15)',
                        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.3)',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '11px',
                        lineHeight: 1,
                        fontWeight: 700,
                        color: '#1976D2'
                    }}
                >
                    {index + 1}
                </Box>
            ))}
        </Paper>
    );
};

export default function HomePageLayout(props) {
    const pBusinessType = props.user?.comp_settings.find(setting => setting.feature_value === 'primaryBusinessType') ? props.user.comp_settings.filter((setting) => setting.feature_value === "primaryBusinessType")[0].feature_id : -1;

    const [selectedLayoutID, setSelectedLayoutID] = useState(null);
    const [selectedLayoutType, setSelectedLayoutType] = useState('1 X 1');
    const [selectedLayoutContent, setSelectedLayoutContent] = useState({});
    const [selectedEmployeeType, setSelectedEmployeeType] = useState('');
    const [employeeMenuItems, setEmployeeMenuItems] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [open, setOpen] = useState(false);
    const [empTypePermissions, setEmpTypePermissions] = useState([]);
    const [permissionsLoading, setPermissionsLoading] = useState(true);

    let resources = entities.resources;
    let predefinedRoles = ['fieldworker', 'supportcoordinator', 'manager', 'admin', 'casemanager'];

    const activeEmployeeTypes = props.employeeTypeList.filter((type) => type.is_Active === 0 || type.is_Active === 3);
    const activeEmployeeTypeKey = activeEmployeeTypes.map((type) => type.EntityValue).sort().join('|');

    useEffect(() => {
        getEmployeeMenuItems();
    }, [props, editMode]);

    useEffect(() => {

        async function fetchPermissions() {
            const updatedEmpTypeList = await Promise.all(
                activeEmployeeTypes.map(async (type) => {

                    const features = {};
                    const batchSize = 50;

                    const normalizedType = String(type.EntityValue).replaceAll(" ", "").toLowerCase();
                    const roleName = predefinedRoles.includes(normalizedType)
                        ? normalizedType
                        : `${normalizedType}_${props.user.comp_id}`;

                    for (let index = 0; index < resources.length; index += batchSize) {
                        const currentBatch = resources.slice(index, Math.min(index + batchSize, resources.length));

                        const checkAccess = await axios.post(CERBOSURL + "/check/resources", {
                            principal: {
                                id: String(props.user.emp_id),
                                roles: [roleName],
                                attr: {
                                    states: props.user.states.map((s) => s.state_id),
                                    modules: props.user.comp_settings.map((f) => f.feature_id),
                                    company_type: pBusinessType,
                                }
                            },
                            resources: currentBatch.map((r) => ({
                                resource: {
                                    ...r.resource,
                                    id: r.resource.id + String(props.user.emp_id),
                                },
                                actions: r.actions,
                            }))
                        });
                        currentBatch.forEach((resource) => {
                            const resourceId = resource.resource.id + String(props.user.emp_id);
                            const result = checkAccess.data.results.find((ele) => ele.resource.id === resourceId);
                            features[String(resource.resource.id).split("#")[0]] = result.actions[resource.actions[0]] === 'EFFECT_ALLOW';
                            // features[`${String(resource.resource.id).split("#")[0]}_${String(resource.resource.kind)}_${String(resource.actions[0])}`] = result.actions[resource.actions[0]] === 'EFFECT_ALLOW';
                        });
                    }

                    if (Object.values(features).every(value => value === false)) {
                        for (let index = 0; index < resources.length; index += batchSize) {
                            let currentBatch = resources.slice(index, Math.min(index + batchSize, resources.length));

                            const checkAccess = await axios.post(CERBOSURL + "/check/resources", {
                                principal: {
                                    id: String(props.user.emp_id),
                                    roles: [normalizedType.endsWith('manager') ? 'manager' : 'fieldworker'],
                                    attr: {
                                        states: props.user.states.map((s) => s.state_id),
                                        modules: props.user.comp_settings.map((f) => f.feature_id),
                                        company_type: pBusinessType,
                                    }
                                },
                                resources: currentBatch.map((r) => ({
                                    resource: {
                                        ...r.resource,
                                        id: r.resource.id + String(props.user.emp_id),
                                    },
                                    actions: r.actions,
                                }))
                            });
                            currentBatch.forEach((resource) => {
                                const resourceId = resource.resource.id + String(props.user.emp_id);
                                const result = checkAccess.data.results.find((ele) => ele.resource.id === resourceId);
                                features[String(resource.resource.id).split("#")[0]] = result.actions[resource.actions[0]] === 'EFFECT_ALLOW';
                                // features[`${String(resource.resource.id).split("#")[0]}_${String(resource.resource.kind)}_${String(resource.actions[0])}`] = result.actions[resource.actions[0]] === 'EFFECT_ALLOW';
                            });
                        }
                    }
                    return { ...type, permissions: features };
                })
            );

            setEmpTypePermissions(updatedEmpTypeList);
        }

        if (activeEmployeeTypes.length === 0) {
            setEmpTypePermissions([]);
            setPermissionsLoading(false);
            return;
        }

        setPermissionsLoading(true);
        fetchPermissions()
            .catch((error) => console.error('Failed to resolve employee type permissions:', error))
            .finally(() => setPermissionsLoading(false));
    }, [activeEmployeeTypeKey])

    // Widgets this employee type is allowed to have on its home page. Cerbos is the only filter — see HOME_WIDGETS.
    const availableWidgets = useMemo(() => {
        const currentPermissions = empTypePermissions.find((empType) => empType.EntityValue === selectedEmployeeType)?.permissions;

        if (!currentPermissions) {
            return [];
        }

        return HOME_WIDGETS.filter((widget) => currentPermissions[widget.permission]).map((widget) => widget.label);
    }, [empTypePermissions, selectedEmployeeType]);

    // A card may already hold a widget that is no longer permitted (permissions
    // changed after the layout was saved). Keep it selectable so editing an
    // unrelated card does not silently blank it out.
    const functionalityMenuItems = useMemo(() => {
        const assigned = Object.values(selectedLayoutContent).filter(Boolean);
        return [...availableWidgets, ...assigned.filter((label) => !availableWidgets.includes(label))];
    }, [availableWidgets, selectedLayoutContent]);

    const getEmployeeMenuItems = async () => {
        const employeeTypeList = activeEmployeeTypes.map((type) => type.EntityValue);

        const layoutEmployeeTypes = new Set(props.layout.filter(layout => layout.companyID !== -1).map(layout => layout.employeeType));
        const filteredEmployeeTypeList = employeeTypeList.filter(type => !layoutEmployeeTypes.has(type));

        setEmployeeMenuItems(editMode ? employeeTypeList : filteredEmployeeTypeList);
    };

    const handleSave = async (mode) => {
        if (mode === 'add') {
            await axios.post(APIURL + "/gridLayout", {
                layoutType: selectedLayoutType,
                layoutContent: JSON.stringify(selectedLayoutContent),
                layoutAddedBy: props.user.emp_id,
                employeeType: selectedEmployeeType,
                companyID: props.user.comp_id,
                companyState: props.user.states[0]?.state_id,
                companyLineOfBusiness: pBusinessType
            }).then((response) => {
                console.log(response.data);
            }).catch((error) => {
                console.error(error);
            });
        } else {
            await axios.put(APIURL + "/gridLayout", {
                layoutID: selectedLayoutID,
                layoutType: selectedLayoutType,
                layoutContent: JSON.stringify(selectedLayoutContent),
                layoutAddedBy: props.user.emp_id,
                employeeType: selectedEmployeeType,
            }).then((response) => {
                console.log(response.data);
            }).catch((error) => {
                console.error(error);
            });
        }

        props.loadUserExperienceData();
        handleClose();
    }

    const handleDelete = async (layoutID) => {
        await axios.delete(APIURL + "/gridLayout", {
            params: {
                layoutID: layoutID,
            }
        }).then((response) => {
            console.log(response.data);
        }).catch((error) => {
            console.error(error);
        });

        props.loadUserExperienceData();
    };

    const getLayoutDetailsComponent = (layout, index) => {
        const layoutConfig = LAYOUT_CONFIGURATIONS[layout.layoutType] || [];
        const contentData = JSON.parse(layout.layoutContent || '{}');

        return (
            <Grid size={12} key={index} sx={{ mb: 1 }}>
                <Box sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1, p: 1, backgroundColor: 'rgba(0,0,0,0.02)' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box>
                            <Box sx={{ alignItems: 'center' }}>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                                    <span style={{ color: 'gray' }}>Employee Type: </span>{layout.employeeType}
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 'medium' }}>
                                    <span style={{ color: 'gray' }}>Layout Type: </span>{layout.layoutType}
                                </Typography>
                                {layoutConfig.map((card, cardIndex) => {
                                    const cardContent = contentData[`card${cardIndex + 1}`] || 'Not assigned';

                                    return (
                                        <Typography key={cardIndex} variant="body2">
                                            <span style={{ color: 'gray' }}>Card {cardIndex + 1}: </span>{cardContent}
                                        </Typography>
                                    );
                                })}
                            </Box>
                        </Box>
                        <Box sx={{ width: '25%' }}>
                            <Paper variant="outlined">
                                <LayoutPreview layoutType={layout.layoutType} />
                            </Paper>
                        </Box>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Box></Box>
                        {layout.companyID !== -1 &&
                            <Box>
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                        setSelectedEmployeeType(layout.employeeType);
                                        setSelectedLayoutID(layout.layoutID);
                                        setSelectedLayoutType(layout.layoutType);
                                        setSelectedLayoutContent(contentData);
                                        setEditMode(true);
                                        setOpen(true);
                                    }}
                                >
                                    <Edit fontSize="small" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => { handleDelete(layout.layoutID); }}
                                >
                                    <DeleteRoundedIcon fontSize="small" />
                                </IconButton>
                            </Box>
                        }
                    </Box>
                </Box>
            </Grid>
        );
    };

    const handleClose = () => {
        setOpen(false);
        setEditMode(false);
        setSelectedLayoutID(null);
        setSelectedLayoutType('1 X 1');
        setSelectedLayoutContent({});
        setSelectedEmployeeType('');
    };

    const handleLayoutTypeSelect = (layoutType) => {
        setSelectedLayoutType(layoutType);
        setSelectedLayoutContent({});
    };

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Grid container spacing={2}>
                <Grid size={props.leftColumnSize}>
                    <Stack spacing={1}>
                        <Typography variant="h5">
                            Home Page Layout
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'gray' }}>
                            Customize the home page by selecting a layout and choosing the functionalities to display
                        </Typography>
                    </Stack>
                    <Box sx={{ mt: 5 }}>
                        <Typography variant="h6">
                            Default layouts
                        </Typography>
                        {props.layout.filter((layout) => layout.companyID === -1).map((layout, index) => {
                            return getLayoutDetailsComponent(layout, index);
                        })}
                    </Box>
                </Grid>
                <Grid size={props.rightColumnSize}>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="h6">
                                Company layouts
                            </Typography>
                            {props.layout.filter((layout) => layout.companyID !== -1).map((layout, index) => {
                                return getLayoutDetailsComponent(layout, index);
                            })}
                            <Button
                                onClick={() => setOpen(true)}
                                sx={{ marginTop: 2 }}
                                variant="contained"
                                color="primary"
                            >
                                + Add New Layout
                            </Button>
                        </Box>
                    </Stack>
                </Grid>

                <Modal enforceFocus={false} open={open} onClose={handleClose}>
                    <Box
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 900,
                            maxWidth: '92vw',
                            maxHeight: '90vh',
                            overflowY: 'auto',
                            bgcolor: 'background.paper',
                            boxShadow: 24,
                            p: 4,
                            borderRadius: 2
                        }}
                    >
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {editMode ? 'Edit Layout' : 'Add New Layout'}
                        </Typography>
                        <Typography variant="h6" sx={{ mb: 1 }}>
                            Select Employee Type
                        </Typography>
                        <FormControl fullWidth sx={{ mb: 3 }}>
                            <InputLabel>Employee Type</InputLabel>
                            <Select
                                id="Employee Type"
                                label="Employee Type"
                                value={selectedEmployeeType || ""}
                                onChange={(e) => {
                                    setSelectedEmployeeType(e.target.value);
                                }}
                            >
                                {employeeMenuItems.map((option, idx) => (
                                    <MenuItem key={idx} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <RadioGroup
                            value={selectedLayoutType}
                            onChange={(e) => handleLayoutTypeSelect(e.target.value)}
                        >
                            <Grid container spacing={2}>
                                {Object.keys(LAYOUT_CONFIGURATIONS).map((layoutType) => (
                                    <Grid size={{ xs: 6, sm: 4, md: 3 }} key={layoutType}>
                                        <Paper
                                            variant="outlined"
                                            onClick={() => handleLayoutTypeSelect(layoutType)}
                                            sx={{
                                                p: 1,
                                                width: '100%',
                                                height: '100%',
                                                cursor: 'pointer',
                                                border: selectedLayoutType === layoutType
                                                    ? '2px solid #1976D2'
                                                    : '1px solid rgba(0,0,0,0.2)',
                                                '&:hover': {
                                                    borderColor: '#1976D2',
                                                    backgroundColor: 'rgba(25, 118, 210, 0.04)'
                                                }
                                            }}
                                        >
                                            <FormControlLabel
                                                value={layoutType}
                                                control={<Radio />}
                                                label={layoutType}
                                                sx={{ width: '100%', pointerEvents: 'none' }}
                                            />
                                            <LayoutPreview layoutType={layoutType} />
                                        </Paper>
                                    </Grid>
                                ))}
                            </Grid>
                        </RadioGroup>
                        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                            Assign functionalities
                        </Typography>
                        {!selectedEmployeeType &&
                            <Typography variant="body1" sx={{ mb: 1, color: 'gray' }}>
                                Select an employee type to see the functionalities available to it
                            </Typography>
                        }
                        {selectedEmployeeType && permissionsLoading &&
                            <Typography variant="body1" sx={{ mb: 1, color: 'gray' }}>
                                Loading the functionalities available to this employee type...
                            </Typography>
                        }
                        {selectedEmployeeType && !permissionsLoading && availableWidgets.length === 0 &&
                            <Typography variant="body1" sx={{ mb: 1, color: 'gray' }}>
                                This employee type's permissions do not allow any home page functionality.
                            </Typography>
                        }
                        <Grid container spacing={2}>
                            {LAYOUT_CONFIGURATIONS[selectedLayoutType]?.map((card, index) => (
                                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                                    <FormControl fullWidth disabled={!selectedEmployeeType}>
                                        <InputLabel>Card {index + 1}</InputLabel>
                                        <Select
                                            id={`card-${index + 1}`}
                                            label={`Card ${index + 1}`}
                                            value={selectedLayoutContent[`card${index + 1}`] || ""}
                                            onChange={(e) => {
                                                setSelectedLayoutContent(prevState => ({
                                                    ...prevState,
                                                    [`card${index + 1}`]: e.target.value
                                                }));
                                            }}
                                        >
                                            {functionalityMenuItems.map((option, idx) => (
                                                <MenuItem key={idx} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            ))}
                        </Grid>
                        <Box sx={{ mt: 2 }}>
                            <SaveButton onClick={() => handleSave(editMode ? 'edit' : 'add')} disabled={!selectedLayoutType || Object.keys(selectedLayoutContent).length === 0 || !selectedEmployeeType} />
                        </Box>
                    </Box>
                </Modal>
            </Grid>
        </Box>
    );
};